import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';

/**
 * Botão "Câmera" da lateral (desktop) e flutuante (mobile) — tira foto pro
 * paciente ABERTO e anexa ao prontuário (pedido de 02/10/2026).
 *
 * Mesmo contrato do `ExtractionContext`/`DictationContext`: a ficha do
 * paciente (`PatientDetailPage`, sempre montada em `/pacientes/:id`) é a
 * dona — registra o handler e abre o modal "Anexar imagens" daquele
 * paciente; a sidebar e o FAB só disparam.
 *
 * O `<input capture>` mora AQUI, no Provider (montado no Shell), e não no
 * botão: no celular o botão do menu lateral fecha o drawer no clique, e um
 * input desmontado perderia a foto quando a câmera nativa devolve.
 */

/** O que o botão entrega pra ficha. */
export interface ClinicalCameraRequest {
  /** Fotos já tiradas pela câmera nativa (celular/tablet). */
  files: File[];
  /** Computador: abrir já na webcam (só quando existe uma). */
  webcam: boolean;
}

type CameraHandler = (req: ClinicalCameraRequest) => void;

interface ClinicalCameraContextValue {
  /** Há uma ficha aberta, com permissão de anexar imagem, escutando. */
  available: boolean;
  /** Clique no botão: câmera nativa (toque) ou webcam/modal (computador). */
  shoot: () => void;
  /** Usado pela ficha pra instalar o handler. Devolve unsubscribe. */
  register: (fn: CameraHandler) => () => void;
}

const ClinicalCameraContext = createContext<ClinicalCameraContextValue | null>(null);

/** Celular/tablet: a câmera do aparelho é o caminho (abre direto, traseira). */
const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

/** Computador sem webcam não pode cair numa tela preta de "sem câmera". */
const hasWebcam = async (): Promise<boolean> => {
  try {
    const devices = await navigator.mediaDevices?.enumerateDevices?.();
    return !!devices?.some((d) => d.kind === 'videoinput');
  } catch {
    return false;
  }
};

export const ClinicalCameraProvider = ({ children }: { children: ReactNode }) => {
  const handlerRef = useRef<CameraHandler | null>(null);
  const [available, setAvailable] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const register = useCallback((fn: CameraHandler) => {
    handlerRef.current = fn;
    setAvailable(true);
    return () => {
      if (handlerRef.current === fn) {
        handlerRef.current = null;
        setAvailable(false);
      }
    };
  }, []);

  const shoot = useCallback(() => {
    if (!handlerRef.current) return;
    if (isTouchDevice()) {
      // Síncrono, ainda dentro do gesto do clique — o Safari do iPhone/iPad
      // ignora `click()` em input de arquivo fora dele.
      inputRef.current?.click();
      return;
    }
    void hasWebcam().then((webcam) => handlerRef.current?.({ files: [], webcam }));
  }, []);

  const onPicked = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length > 0) handlerRef.current?.({ files, webcam: false });
  }, []);

  const value = useMemo(() => ({ available, shoot, register }), [available, shoot, register]);

  return (
    <ClinicalCameraContext.Provider value={value}>
      {children}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={onPicked}
      />
    </ClinicalCameraContext.Provider>
  );
};

export const useOptionalClinicalCamera = () => useContext(ClinicalCameraContext);
